<template>
  <div class="Carousel">
    <div v-show="currentIndex > 0" class="scrollerPrev" :style="{height: `${imageHeight}px`}" @click="activate(currentIndex - 1)"></div>
    <div ref="element" class="CarouselContents" @scroll="handleScroll" :style="{height: `${imageHeight}px`}">
      <ul class="CarouselList">
        <li
          v-for="(content, index) in contents"
          :key="index"
          class="CarouselItem"
          :class="{edge: index + 1 === contents.length}"
        >
          <NuxtLink v-if="!!content.link" :to="content.link" :target="content.isLinkExternal ? '_brank' : undefined">
            <img class="content" :src="content.src" :alt="content.alt" />
          </NuxtLink>
          <img v-else class="content" :src="content.src" :alt="content.alt" />
        </li>
      </ul>
    </div>
    <div class="CarouselDots">
      <button
        v-for="(_, index) in contents"
        :key="index"
        class="dot"
        @click="activate(index)"
        aria-label="Page"
      >
        <div
          class="CarouselIcon"
          :class="{
            active: isActive(index),
            inactive: !isActive(index)
          }"
        >
        </div>
      </button>
    </div>
    <div v-show="currentIndex + 1 < contents.length" class="scrollerNext" :style="{height: `${imageHeight}px`}" @click="activate(currentIndex + 1)"></div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted, onBeforeUnmount } from 'vue';
import { Ball } from '@ka-nabellinc/ka-nabell-icons';
import { MIN_INTERVAL, CONTENTS_GAP, CONTENTS_SCALE } from './constants'

export default defineComponent({
  components: {
    Ball,
  },
  props: {
    contents: {
      type: Array as () => {
        src: string,
        link?: string,
        alt?: string,
        isLinkExternal?: boolean,
      }[],
      default: []
    },
    interval: {
      type: Number,
      default: 3000,
    },
  },
  setup(props) {
    const element = ref<HTMLDivElement>()
    const currentIndex = ref<number>(0)
    const scrollWidth = ref<number>(0)
    const clicking = ref<boolean>()
    const scrollingWithAnimation = ref<boolean>()
    const animation = ref<number>()
    const imageWidth = ref<number>()

    // アスペクト比から画像の高さを計算
    // Reference: https://docs.google.com/document/d/16oLURee9rZNXyVgpkvxBZ-VdsvWzuGp5-4NaZG1infk/edit#heading=h.a02r92ymr753
    const imageHeight = computed<number | undefined>(
      () => typeof imageWidth.value === 'number' ? imageWidth.value * 563 / 1000 : undefined
    )

    onMounted(() => {
      if (typeof window !== 'undefined') {
        setScrollWidth()
        setImageWidth()
  
        startAnimation()
  
        window.addEventListener('resize', handleResize);
      }
    })

    onBeforeUnmount(() => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize)
        clearInterval(animation.value)
      }
    })

    const handleResize = () => {
      setScrollWidth()
      setImageWidth()
    }

    const startAnimation = () => {
      if (typeof window !== 'undefined') {
        // scrollendの判定が全ブラウザでできないため、setTimeoutでスライド距離に応じた時間後にscrollend処理を走らせる
        // スライド距離が一定を超えると、animationのintervalをスライド時間(scrollendまでにかかる時間)が越えるので、intervalの最小時間を調整
        const limitSize = 8
        const limitInterval = props.contents.length < limitSize ? 
          MIN_INTERVAL : MIN_INTERVAL + (props.contents.length - (limitSize - 1)) * 100
        const interval = props.interval < limitInterval ? limitInterval : props.interval

        animation.value = window.setInterval(() => {
          if (!element.value) return

          // スクロールされる image 枚数
          let scrolledImgNums = 0
          if (currentIndex.value < props.contents.length - 1) {
            // 一番右端以外は、右に1枚分スクロールされる
            scrolledImgNums = 1
            currentIndex.value = currentIndex.value + 1
          } else {
            // 一番右端の場合は左端までスクロール
            scrolledImgNums = props.contents.length - 1
            currentIndex.value = 0
          }

          scrollingWithAnimation.value = true
          element.value.scrollLeft = (scrollWidth.value - 12) * currentIndex.value
          // scrollendの判定が全ブラウザでできないため、setTimeoutでスライド距離に応じた時間後にscrollend処理を走らせる
          window.setTimeout(() => {
            scrollingWithAnimation.value = false
          }, culcScrollTime(scrolledImgNums))

        }, interval)
      }
    }

    const culcScrollTime = (scrolledImgNums: number) => {
      const baseTimeMs = typeof imageWidth.value === 'number' && imageWidth.value < 300 ? 500 : 800
      if (scrolledImgNums <= 3) {
        return baseTimeMs
      }

      return baseTimeMs + (scrolledImgNums - 3) * 120
    }

    const setScrollWidth = () => {
      if (typeof window !== 'undefined') {
        if (!element.value) return
        const styles = window.getComputedStyle(element.value)
        const width = Number.parseFloat(styles.width)
        scrollWidth.value = Math.round(width * CONTENTS_SCALE) + CONTENTS_GAP
      }
    }

    const scrollToSpecifiedIndex = (index: number) => {
      if (typeof window !== 'undefined') {
        if (!element.value) return
        const distance = Math.abs(currentIndex.value - index)

        currentIndex.value = index

        clicking.value = true
        element.value.scrollLeft = (scrollWidth.value - CONTENTS_GAP) * index
        // scrollendの判定が全ブラウザでできないため、setTimeoutでスライド距離に応じた時間後にscrollend処理を走らせる
        window.setTimeout(() => {
          clicking.value = false
        }, culcScrollTime(distance))
      }
    }

    const handleScroll = () => {
      if (!element.value) return

      if (!scrollingWithAnimation.value) {
        clearInterval(animation.value)
      }

      if (!clicking.value && !scrollingWithAnimation.value) {
        const scrollPosition = element.value.scrollLeft ? element.value.scrollLeft : 0
        currentIndex.value = !scrollWidth.value ? 0 : Math.round(scrollPosition / scrollWidth.value)
      }
    }

    const setImageWidth = () => {
      if (!element.value) return
      const ImageWidthRate = 85
      const carouselContentsWidth = element.value.clientWidth
      imageWidth.value = carouselContentsWidth * ImageWidthRate / 100
    }

    const activate = (index: number) => {
      if (isActive(index)) {
        return
      }

      if (!validateIndex(index)) {
        return
      }

      scrollToSpecifiedIndex(index)
    }

    const isActive = (index: number) => currentIndex.value === index
    const validateIndex = (index: number) => index >= 0 && index < props.contents.length

    return {
      element,
      currentIndex,
      scrollWidth,
      imageHeight,
      handleScroll,
      activate,
      isActive,
    }
  },
})
</script>

<style scoped>
.Carousel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  position: relative;
}

.CarouselContents {
  overflow: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  width: 100%;
  -ms-overflow-style: none;
  scrollbar-width: none;
  /* 利用側のpadding に依存するが例外的に許容 */
  min-height: calc(((100vw - 32px) * 85 / 100) * 563 / 1000); /* 画像幅(ビューポート幅 - padding(32px) * 85%) * アスペクト比(1 / 0.563) */
}

@media (min-width: 430px) {
  /* 利用側の layout に依存するが例外的に許容 */
  .CarouselContents {
    min-height: calc(((430px - 32px) * 85 / 100) * 563 / 1000);
  }
}

.CarouselContents::-webkit-scrollbar {
  display:none;
}

.CarouselList {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
}

.CarouselItem {
  list-style: none;
  min-width: 85%;
  height: 100%;
  scroll-snap-align: start;
  position: relative;
}

.CarouselItem img {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  object-fit: cover;
  position: absolute;
  left: 9%;
}

.CarouselItem.edge::after {
  content: "";
  display: inline-block;
  width: 15%;
  height: 100%;
  position: absolute;
  left: 108%;
}

.CarouselDots {
  display: flex;
  gap: 8px;
}

.CarouselDots .dot {
  background-color: transparent;
  border: none;
  cursor: pointer;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  position: relative;
  -webkit-tap-highlight-color:rgba(0,0,0,0);
}

.CarouselDots .inactive {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--primitive-colors-neutral-white-600);
}

.CarouselDots .active {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--primitive-colors-neutral-white-400);
  border: 3px solid var(--primitive-colors-basic-yellow-500);
}

.CarouselIcon {
  position: absolute;
  left: 0;
  top: 0;
}

.CarouselIcon.active {
  left: -1px;
  top: -1px;
}

.content {
  aspect-ratio: 1 / 0.563; /* Reference: https://docs.google.com/document/d/16oLURee9rZNXyVgpkvxBZ-VdsvWzuGp5-4NaZG1infk/edit#heading=h.a02r92ymr753 */
}

.scrollerPrev {
  /* image's width(85%) * image's left(9%) = 7.65% - gap(12px) */
  width: calc(7.65% - 12px);
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  background: transparent;
  cursor: pointer;
}

.scrollerNext {
  width: calc(7.65%  - 12px);
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  background: transparent;
  cursor: pointer;
}
</style>